"use client";

import {
  TableHead,
  TableHeader,
  TableRow,
} from "@nyntax/design-system/components/ui/table";
import type { Header } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import { useDataTable } from "./data-table-context";

const SORT_ICONS = {
  asc: (
    <LuChevronUp aria-hidden="true" className="size-4 shrink-0 opacity-80" />
  ),
  desc: (
    <LuChevronDown aria-hidden="true" className="size-4 shrink-0 opacity-80" />
  ),
};

function SortableHeader({ header }: { header: Header<unknown, unknown> }) {
  return (
    <button
      className="flex h-full w-full cursor-pointer select-none items-center justify-between gap-2 bg-transparent"
      onClick={header.column.getToggleSortingHandler()}
      type="button"
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {SORT_ICONS[header.column.getIsSorted() as "asc" | "desc"] ?? null}
    </button>
  );
}

function renderHeaderCell(header: Header<unknown, unknown>) {
  if (header.isPlaceholder) {
    return null;
  }
  if (!header.isPlaceholder && header.column.getCanSort()) {
    return <SortableHeader header={header} />;
  }
  return flexRender(header.column.columnDef.header, header.getContext());
}

function DataTableHeader() {
  const table = useDataTable();

  return (
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow
          className="bg-muted/40 hover:bg-muted/40"
          key={headerGroup.id}
        >
          {headerGroup.headers.map((header) => {
            const size = header.column.getSize();

            return (
              <TableHead
                className="sticky top-0 z-10 bg-muted/40"
                key={header.id}
                style={size > 0 ? { width: `${size}px` } : {}}
              >
                {renderHeaderCell(header)}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}

export { DataTableHeader };
